/* ==========================================================================
   NWP MEGA MENU — scoped progressive enhancement for the frozen live header.

   Prestige retains disclosure, drawer, animation, and sticky-header ownership.
   This module adds only NWP keyboard entry and compact-drawer layer semantics.
   ========================================================================== */

const COMPACT_SIDEBAR_QUERY = '(max-width: 999px)';
const MAIN_PANEL_RESTORE_DEADLINE_MS = 1200;
const TOP_LEVEL_DISCLOSURE_SELECTOR = [
  '.header__primary-nav-item > mega-menu-disclosure',
  '.header__primary-nav-item > dropdown-menu-disclosure',
].join(', ');
const ACCOUNT_DISCLOSURE_SELECTOR = '.header__secondary-nav .nwp-account--header > dropdown-menu-disclosure';
const COORDINATED_DISCLOSURE_SELECTOR = `${TOP_LEVEL_DISCLOSURE_SELECTOR}, ${ACCOUNT_DISCLOSURE_SELECTOR}`;
const RUNTIME_KEY = Symbol.for('nwp.mega-menu.runtime');

function disclosureDetails(disclosure) {
  return disclosure?.querySelector(':scope > details') ?? null;
}

function disclosureIsOpen(disclosure) {
  return Boolean(disclosureDetails(disclosure)?.matches('details[open]'));
}

function closeDisclosure(disclosure, instant = false) {
  if (!disclosureIsOpen(disclosure)) return;
  const details = disclosureDetails(disclosure);
  if (typeof disclosure.toggle === 'function') {
    /* Sibling handoffs must remove native state before Prestige closes. This
       lets its public cleanup run without creating a hide animation whose
       delayed completion could close a disclosure after it is reopened. */
    if (instant) details.removeAttribute('open');
    disclosure.toggle(false);
    return;
  }

  /* Fail closed if a future Prestige version removes the public method. */
  details?.setAttribute('aria-expanded', 'false');
  details?.removeAttribute('open');
}

function hasVisibleBox(element) {
  if (!element?.isConnected) return false;
  const rect = element.getBoundingClientRect();
  const style = getComputedStyle(element);
  return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden';
}

function normalizeSidebarSemantics(sidebar) {
  sidebar
    ?.querySelectorAll('header-sidebar-collapsible-panel')
    .forEach((panel) => panel.setAttribute('role', 'group'));
}

function activeSidebarPanel(collapsiblePanel) {
  const activeId = collapsiblePanel?.getAttribute('aria-activedescendant');
  if (!activeId) return null;

  return Array.from(collapsiblePanel.querySelectorAll('.header-sidebar__sub-panel')).find(
    (panel) => panel.id === activeId,
  ) ?? null;
}

function syncCompactSidebarLayers(sidebar, focusActivePanel = false) {
  if (!sidebar) return;

  const mainPanel = sidebar.querySelector('.header-sidebar__main-panel');
  const collapsiblePanel = sidebar.querySelector('header-sidebar-collapsible-panel');
  if (!mainPanel || !collapsiblePanel) return;

  const concealMainPanel = Boolean(
    window.matchMedia(COMPACT_SIDEBAR_QUERY).matches
      && (
        collapsiblePanel.hasAttribute('open')
        || collapsiblePanel.hasAttribute('data-nwp-layer-closing')
      )
  );

  if (concealMainPanel && focusActivePanel) {
    const activePanel = activeSidebarPanel(collapsiblePanel);
    const focusTarget = activePanel?.querySelector('[data-action="close-panel"]');
    if (focusTarget && !activePanel.contains(document.activeElement)) {
      focusTarget.focus({ preventScroll: true });
    }
  }

  /* Use the content attribute so Prestige's focus-trap selector also honors
     isolation on engines predating native HTMLElement.inert support. */
  mainPanel.toggleAttribute('inert', concealMainPanel);
  if (concealMainPanel) {
    mainPanel.setAttribute('aria-hidden', 'true');
  } else {
    mainPanel.removeAttribute('aria-hidden');
  }
}

function createHeaderController(header, runtime) {
  const nav = header.querySelector('.header__primary-nav');
  if (!nav) return null;

  const abortController = new AbortController();
  const { signal } = abortController;
  let disposed = false;
  let hoverBridgeFrame = null;
  let sidebarOpener = null;
  let subPanelOpener = null;
  let pointerPosition = { x: 0, y: 0 };
  let dismissedAtPointer = null;

  const topLevelDisclosures = () => Array.from(nav.querySelectorAll(TOP_LEVEL_DISCLOSURE_SELECTOR));
  const coordinatedDisclosures = () => Array.from(header.querySelectorAll(COORDINATED_DISCLOSURE_SELECTOR));
  const closeSiblingDisclosures = (entered) => {
    for (const disclosure of coordinatedDisclosures()) {
      if (disclosure !== entered) closeDisclosure(disclosure, true);
    }
  };
  const positionHoverBridge = (disclosure) => {
    const details = disclosureDetails(disclosure);
    const summary = details?.querySelector(':scope > summary');
    const anchor = details?.querySelector(':scope > .nwp-mega-menu__anchor');
    if (!summary || !anchor || !hasVisibleBox(summary)) return;

    const containingBlock = anchor.offsetParent;
    const containingRect = containingBlock?.getBoundingClientRect() ?? header.getBoundingClientRect();
    const headerRect = header.getBoundingClientRect();
    const summaryRect = summary.getBoundingClientRect();
    const panelGapToken = getComputedStyle(header).getPropertyValue('--nwp-mega-panel-gap').trim();
    const panelGapValue = Number.parseFloat(panelGapToken) || 0.75;
    const panelGap = panelGapToken.endsWith('rem')
      ? panelGapValue * Number.parseFloat(getComputedStyle(document.documentElement).fontSize)
      : panelGapValue;
    const panelTop = headerRect.bottom + panelGap;
    const inlinePadding = 16;

    anchor.style.setProperty('--nwp-bridge-left', `${summaryRect.left - containingRect.left - inlinePadding}px`);
    anchor.style.setProperty('--nwp-bridge-top', `${summaryRect.bottom - containingRect.top}px`);
    anchor.style.setProperty('--nwp-bridge-width', `${summaryRect.width + inlinePadding * 2}px`);
    anchor.style.setProperty('--nwp-bridge-height', `${Math.max(1, panelTop - summaryRect.bottom)}px`);
    anchor.setAttribute('data-nwp-bridge-ready', '');
  };
  const positionHoverBridges = () => {
    for (const disclosure of topLevelDisclosures()) positionHoverBridge(disclosure);
  };
  const scheduleHoverBridgePosition = () => {
    if (disposed || hoverBridgeFrame !== null) return;
    hoverBridgeFrame = requestAnimationFrame(() => {
      hoverBridgeFrame = null;
      if (!disposed) positionHoverBridges();
    });
  };

  // Scrolling or removing a panel can emit mouseover without pointer movement.
  // After Escape, that event must not reopen the disclosure under the pointer.
  document.addEventListener('pointermove', (event) => {
    if (!event.isTrusted || event.pointerType === 'touch') return;
    if (event.clientX !== pointerPosition.x || event.clientY !== pointerPosition.y) dismissedAtPointer = null;
    pointerPosition = { x: event.clientX, y: event.clientY };
  }, { passive: true, signal });
  header.addEventListener('mouseover', (event) => {
    if (!dismissedAtPointer || !(event.target instanceof Element)
      || !event.target.closest(COORDINATED_DISCLOSURE_SELECTOR)) return;
    if (event.clientX === dismissedAtPointer.x && event.clientY === dismissedAtPointer.y) {
      event.stopImmediatePropagation();
    } else {
      dismissedAtPointer = null;
    }
  }, { capture: true, signal });

  /* Prestige 11.4.0 compares a native <details> node with its custom-element
     wrapper when it tries to close an adjacent hover disclosure. Coordinate
     siblings at the shared nav boundary so exactly one can remain open. Close
     the old sibling synchronously so its delayed hide cannot race a re-entry. */
  header.addEventListener(
    'mouseover',
    (event) => {
      if (!window.matchMedia('screen and (pointer: fine)').matches) return;
      const entered = event.target instanceof Element
        ? event.target.closest(COORDINATED_DISCLOSURE_SELECTOR)
        : null;
      if (!entered || !header.contains(entered)) return;
      positionHoverBridge(entered);
      closeSiblingDisclosures(entered);
    },
    { signal },
  );
  header.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('summary')) return;
      const entered = event.target.closest(COORDINATED_DISCLOSURE_SELECTOR);
      if (!entered || !header.contains(entered)) return;
      closeSiblingDisclosures(entered);
    },
    { capture: true, signal },
  );
  document.addEventListener('pointerdown', (event) => {
    for (const disclosure of topLevelDisclosures()) {
      if (!event.composedPath().includes(disclosure)) closeDisclosure(disclosure, true);
    }
  }, { capture: true, signal });
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape' && event.code !== 'Escape') return;

      if (
        window.matchMedia(COMPACT_SIDEBAR_QUERY).matches
        && sidebarPanel?.hasAttribute('open')
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        sidebarPanel.removeAttribute('aria-activedescendant');
        return;
      }

      const openDisclosures = coordinatedDisclosures().filter(disclosureIsOpen);
      if (openDisclosures.length === 0) return;
      const expandedDisclosures = openDisclosures.filter(disclosure => disclosureDetails(disclosure)?.getAttribute('aria-expanded') === 'true');
      dismissedAtPointer = { ...pointerPosition };

      const focusedDisclosure = document.activeElement instanceof Element
        ? document.activeElement.closest(COORDINATED_DISCLOSURE_SELECTOR)
        : null;
      for (const disclosure of openDisclosures) closeDisclosure(disclosure, true);
      // Settle an outgoing sibling without taking Escape or focus from Search
      // or Cart. Hover dismissal also preserves unrelated page focus.
      if (expandedDisclosures.length === 0) return;
      if (focusedDisclosure && header.contains(focusedDisclosure)) {
        disclosureDetails(focusedDisclosure)?.querySelector(':scope > summary')?.focus({ preventScroll: true });
      }
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    { capture: true, signal },
  );

  /* Safari can omit anchors from ordinary Tab navigation depending on the
     platform keyboard-navigation preference. Enter the open NWP panel
     deterministically, then leave all subsequent Tab order native. */
  nav.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Tab' || event.shiftKey || !(event.target instanceof Element)) return;

      const summary = event.target.closest('summary');
      const details = summary?.parentElement;
      const disclosure = details?.parentElement;
      if (
        !summary
        || !disclosure?.matches('mega-menu-disclosure.nwp-mega-menu-disclosure')
        || !details.open
      ) {
        return;
      }

      const firstLink = details.querySelector('[data-nwp-mega-menu] a[href]');
      if (!firstLink) return;

      event.preventDefault();
      firstLink.focus({ preventScroll: true });
    },
    { signal },
  );

  const sidebar = header.querySelector('header-sidebar');
  const sidebarPanel = sidebar?.querySelector('header-sidebar-collapsible-panel');
  const compactSidebar = window.matchMedia(COMPACT_SIDEBAR_QUERY);
  let restoreFrame = 0;
  let restoreTimer = 0;
  let restoreGeneration = 0;

  sidebar?.addEventListener('click', (event) => {
    const opener = event.target.closest?.('.header-sidebar__main-panel button[aria-controls]');
    if (opener) subPanelOpener = opener;
  }, { capture: true, signal });

  nav.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element) || !sidebar?.id) return;
      const control = event.target.closest('button[aria-controls]');
      if (control?.getAttribute('aria-controls') === sidebar.id) sidebarOpener = control;
    },
    { capture: true, signal },
  );

  const normalizeOpenSidebar = () => normalizeSidebarSemantics(sidebar);
  const cancelMainPanelRestore = () => {
    restoreGeneration += 1;
    cancelAnimationFrame(restoreFrame);
    clearTimeout(restoreTimer);
    restoreFrame = 0;
    restoreTimer = 0;
  };
  const beginMainPanelRestore = () => {
    cancelMainPanelRestore();
    sidebarPanel?.setAttribute('data-nwp-layer-closing', '');
    syncCompactSidebarLayers(sidebar);
  };
  const finishMainPanelRestore = () => {
    cancelMainPanelRestore();
    if (!sidebar?.open) {
      sidebarPanel?.removeAttribute('data-nwp-layer-closing');
      syncCompactSidebarLayers(sidebar);
      return;
    }
    const generation = restoreGeneration;
    const revealControls = typeof sidebar?.revealItems === 'function'
      ? sidebar.revealItems(false)
      : null;
    let settled = false;
    const releaseMainPanel = (deadlineReached = false) => {
      if (settled || generation !== restoreGeneration) return;
      settled = true;
      clearTimeout(restoreTimer);
      restoreTimer = 0;
      if (deadlineReached && typeof revealControls?.finish === 'function') {
        try {
          revealControls.finish();
        } catch {
          /* The bounded fallback below remains authoritative if a future
             Prestige animation controller rejects finish(). */
        }
      }
      restoreFrame = requestAnimationFrame(() => {
        if (generation !== restoreGeneration) return;
        restoreFrame = 0;
        if (!sidebar?.hasAttribute('open')) {
          sidebarPanel?.removeAttribute('data-nwp-layer-closing');
          syncCompactSidebarLayers(sidebar);
          return;
        }
        if (
          sidebarPanel?.hasAttribute('open')
          || sidebarPanel?.hasAttribute('aria-activedescendant')
        ) {
          sidebarPanel.removeAttribute('data-nwp-layer-closing');
          syncCompactSidebarLayers(sidebar);
          return;
        }
        const mainPanel = sidebar?.querySelector('.header-sidebar__main-panel');
        const interactiveElements = mainPanel?.querySelectorAll(
          'a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [];
        const allInteractiveElementsVisible = Array.from(interactiveElements).every((element) => {
          let opacity = 1;
          for (let current = element; current instanceof Element; current = current.parentElement) {
            opacity *= Number.parseFloat(getComputedStyle(current).opacity || '1');
            if (current === mainPanel) break;
          }
          return opacity >= 0.99;
        });
        if (mainPanel && !allInteractiveElementsVisible) {
          /* Never expose an invisible focus layer and never spin forever.
             Closing the drawer is the fail-safe; a later open starts from
             Prestige's normal reveal lifecycle. */
          if (typeof sidebar.hide === 'function') sidebar.hide();
          return;
        }
        sidebarPanel?.removeAttribute('data-nwp-layer-closing');
        syncCompactSidebarLayers(sidebar);
        sidebar?.querySelectorAll('.header-sidebar__main-panel button[aria-expanded]')
          .forEach((control) => control.setAttribute('aria-expanded', 'false'));
        const active = document.activeElement;
        if (subPanelOpener && (active === document.body || active === sidebar || sidebarPanel?.contains(active))) {
          subPanelOpener.focus({ preventScroll: true });
        }
        subPanelOpener = null;
      });
    };
    const finished = revealControls?.finished;
    if (finished && typeof finished.then === 'function') {
      Promise.resolve(finished).then(
        () => releaseMainPanel(false),
        () => releaseMainPanel(true),
      );
    } else {
      restoreFrame = requestAnimationFrame(() => releaseMainPanel(false));
    }
    restoreTimer = window.setTimeout(
      () => releaseMainPanel(true),
      MAIN_PANEL_RESTORE_DEADLINE_MS,
    );
  };
  const resetSubPanel = () => {
    if (sidebarPanel?.hasAttribute('aria-activedescendant')) {
      sidebarPanel.removeAttribute('aria-activedescendant');
    } else if (sidebarPanel?.hasAttribute('open') && typeof sidebarPanel.hide === 'function') {
      sidebarPanel.hide();
    }
  };
  const activateSubPanel = () => {
    cancelMainPanelRestore();
    sidebarPanel?.removeAttribute('data-nwp-layer-closing');
    normalizeOpenSidebar();
    requestAnimationFrame(() => syncCompactSidebarLayers(sidebar, true));
  };
  const prepareOpenSidebar = () => {
    cancelMainPanelRestore();
    sidebarPanel?.removeAttribute('data-nwp-layer-closing');
    normalizeOpenSidebar();
    syncCompactSidebarLayers(sidebar);
  };
  const alignSidebarClose = () => {
    const opener = sidebarOpener || header.querySelector(`button[aria-controls="${sidebar.id}"]`);
    if (!hasVisibleBox(opener)) return;
    const rect = opener.getBoundingClientRect();
    const center = rect.left + rect.width / 2;
    const inlineCenter = getComputedStyle(sidebar).direction === 'rtl'
      ? document.documentElement.clientWidth - center : center;
    sidebar.style.setProperty('--nwp-sidebar-close-inline', `${inlineCenter}px`);
    sidebar.style.setProperty('--nwp-sidebar-close-block', `${rect.top + rect.height / 2}px`);
    sidebar.style.setProperty('--nwp-sidebar-header-size', `${header.getBoundingClientRect().bottom}px`);
  };

  sidebar?.addEventListener(
    'dialog:before-show',
    (event) => {
      if (event.target !== sidebar) return;
      prepareOpenSidebar();
      sidebarOpener ||= header.querySelector(`button[aria-controls="${sidebar.id}"]`);
      alignSidebarClose();
      queueMicrotask(normalizeOpenSidebar);
    },
    { signal },
  );
  sidebar?.addEventListener('dialog:after-show', (event) => {
    if (event.target !== sidebar) return;
    prepareOpenSidebar();
    // Prestige reparents the drawer after before-show, which drops focus.
    // Touch has no native autofocus, so hand focus over once the move and
    // opening animation finish. Preserve any control already used inside it.
    if (!sidebar.contains(document.activeElement)) sidebar.focus({ preventScroll: true });
  }, { signal });
  sidebarPanel?.addEventListener('dialog:after-show', activateSubPanel, { signal });
  sidebarPanel?.addEventListener('dialog:before-hide', beginMainPanelRestore, { signal });
  sidebarPanel?.addEventListener('dialog:after-hide', finishMainPanelRestore, { signal });
  sidebar?.addEventListener('dialog:before-hide', resetSubPanel, { signal });
  sidebar?.addEventListener(
    'dialog:after-hide',
    () => {
      if (sidebar.open) return;
      cancelMainPanelRestore();
      sidebarPanel?.removeAttribute('data-nwp-layer-closing');
      syncCompactSidebarLayers(sidebar);
      subPanelOpener = null;
      const opener = sidebarOpener;
      sidebarOpener = null;
      requestAnimationFrame(() => {
        if (sidebar.open) return;
        if (hasVisibleBox(opener)) {
          opener.focus({ preventScroll: true });
          return;
        }
        const fallback = header.querySelector('.header__logo, .header__primary-nav a, .header__secondary-nav a');
        if (hasVisibleBox(fallback)) fallback.focus({ preventScroll: true });
      });
    },
    { signal },
  );
  compactSidebar.addEventListener(
    'change',
    (event) => {
      if (!event.matches && sidebar?.hasAttribute('open')) {
        resetSubPanel();
        if (typeof sidebar.hide === 'function') sidebar.hide();
        return;
      }
      syncCompactSidebarLayers(sidebar);
    },
    { signal },
  );

  const sidebarObserver = sidebarPanel
    ? new MutationObserver(() => syncCompactSidebarLayers(sidebar))
    : null;
  sidebarObserver?.observe(sidebarPanel, {
    attributes: true,
    attributeFilter: ['aria-activedescendant', 'open', 'data-nwp-layer-closing'],
  });

  normalizeSidebarSemantics(sidebar);
  syncCompactSidebarLayers(sidebar);
  positionHoverBridges();
  scheduleHoverBridgePosition();
  window.addEventListener('resize', () => {
    scheduleHoverBridgePosition();
    if (sidebar?.open) alignSidebarClose();
  }, { passive: true, signal });
  void document.fonts?.ready
    .then(() => {
      scheduleHoverBridgePosition();
    })
    .catch(() => {});

  function dispose() {
    if (disposed) return;
    disposed = true;
    if (hoverBridgeFrame !== null) cancelAnimationFrame(hoverBridgeFrame);
    hoverBridgeFrame = null;
    cancelMainPanelRestore();
    sidebarObserver?.disconnect();
    abortController.abort();
    runtime.controllers.delete(header);
  }

  header.closest('.shopify-section')?.addEventListener('shopify:section:unload', dispose, {
    once: true,
    signal,
  });

  return { dispose };
}

function headersWithin(root) {
  const headers = [];
  if (root instanceof Element && root.matches('x-header')) headers.push(root);
  root.querySelectorAll?.('x-header').forEach((header) => headers.push(header));
  return headers;
}

function initializeHeaders(root, runtime) {
  for (const header of headersWithin(root)) {
    if (
      runtime.controllers.has(header)
    ) continue;
    const controller = createHeaderController(header, runtime);
    if (controller) runtime.controllers.set(header, controller);
  }
}

function startRuntime() {
  const existing = window[RUNTIME_KEY];
  if (existing) {
    initializeHeaders(document, existing);
    return existing;
  }

  const runtime = {
    abortController: new AbortController(),
    controllers: new WeakMap(),
  };
  window[RUNTIME_KEY] = runtime;
  const { signal } = runtime.abortController;

  document.addEventListener(
    'shopify:section:load',
    (event) => initializeHeaders(event.target, runtime),
    { signal },
  );
  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      for (const header of headersWithin(event.target)) {
        runtime.controllers.get(header)?.dispose();
      }
    },
    { signal },
  );

  initializeHeaders(document, runtime);
  return runtime;
}

startRuntime();
